import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';
import type {
  Kennenlernen,
  CreateKennenlernenDto,
  UpdateKennenlernenDto,
  QueryKennenlernenDto,
  KennenlernenStatistics,
} from '../types/kennenlernen';
import type { ApiResponse } from '../types/bewerber';
import { bedarfeKeys } from './bedarfe';

// Query keys
export const kennenlernenKeys = {
  all: ['kennenlernen'] as const,
  lists: () => [...kennenlernenKeys.all, 'list'] as const,
  list: (filters: QueryKennenlernenDto) => [...kennenlernenKeys.lists(), filters] as const,
  kanban: () => [...kennenlernenKeys.all, 'kanban'] as const,
  details: () => [...kennenlernenKeys.all, 'detail'] as const,
  detail: (id: string) => [...kennenlernenKeys.details(), id] as const,
  statistics: () => [...kennenlernenKeys.all, 'statistics'] as const,
  byBedarf: (bedarfId: string) => [...kennenlernenKeys.all, 'bedarf', bedarfId] as const,
  byBewerber: (bewerberId: string) => [...kennenlernenKeys.all, 'bewerber', bewerberId] as const,
};

// API functions
const fetchKennenlernenList = async (query: QueryKennenlernenDto): Promise<ApiResponse<Kennenlernen[]>> => {
  const params = new URLSearchParams();
  if (query.bedarfId) params.append('bedarfId', query.bedarfId);
  if (query.bewerberId) params.append('bewerberId', query.bewerberId);
  if (query.status) params.append('status', query.status);
  if (query.location) params.append('location', query.location);
  if (query.search) params.append('search', query.search);
  if (query.dateFrom) params.append('dateFrom', query.dateFrom);
  if (query.dateTo) params.append('dateTo', query.dateTo);
  if (query.page) params.append('page', query.page.toString());
  if (query.limit) params.append('limit', query.limit.toString());
  if (query.sortBy) params.append('sortBy', query.sortBy);
  if (query.sortOrder) params.append('sortOrder', query.sortOrder);

  const response = await apiClient.get(`/kennenlernen?${params.toString()}`);
  return response.data;
};

const fetchKennenlernenKanban = async (): Promise<ApiResponse<Kennenlernen[]>> => {
  const response = await apiClient.get('/kennenlernen?limit=200');
  return response.data;
};

const fetchKennenlernen = async (id: string): Promise<ApiResponse<Kennenlernen>> => {
  const response = await apiClient.get(`/kennenlernen/${id}`);
  return response.data;
};

const fetchKennenlernenStatistics = async (): Promise<ApiResponse<KennenlernenStatistics>> => {
  const response = await apiClient.get('/kennenlernen/statistics');
  return response.data;
};

const createKennenlernen = async (data: CreateKennenlernenDto): Promise<ApiResponse<Kennenlernen>> => {
  const response = await apiClient.post('/kennenlernen', data);
  return response.data;
};

const updateKennenlernen = async ({
  id,
  data,
}: {
  id: string;
  data: UpdateKennenlernenDto;
}): Promise<ApiResponse<Kennenlernen>> => {
  const response = await apiClient.patch(`/kennenlernen/${id}`, data);
  return response.data;
};

const deleteKennenlernen = async (id: string): Promise<void> => {
  await apiClient.delete(`/kennenlernen/${id}`);
};

// React Query Hooks

export function useKennenlernenList(query: QueryKennenlernenDto = {}) {
  return useQuery({
    queryKey: kennenlernenKeys.list(query),
    queryFn: () => fetchKennenlernenList(query),
  });
}

export function useKennenlernenKanban() {
  return useQuery({
    queryKey: kennenlernenKeys.kanban(),
    queryFn: fetchKennenlernenKanban,
  });
}

export function useKennenlernen(id: string) {
  return useQuery({
    queryKey: kennenlernenKeys.detail(id),
    queryFn: () => fetchKennenlernen(id),
    enabled: !!id,
  });
}

export function useKennenlernenStatistics() {
  return useQuery({
    queryKey: kennenlernenKeys.statistics(),
    queryFn: fetchKennenlernenStatistics,
  });
}

export function useKennenlernenByBedarf(bedarfId: string) {
  return useQuery({
    queryKey: kennenlernenKeys.byBedarf(bedarfId),
    queryFn: () => fetchKennenlernenList({ bedarfId, limit: 100 }),
    enabled: !!bedarfId,
  });
}

export function useKennenlernenByBewerber(bewerberId: string) {
  return useQuery({
    queryKey: kennenlernenKeys.byBewerber(bewerberId),
    queryFn: () => fetchKennenlernenList({ bewerberId, limit: 100 }),
    enabled: !!bewerberId,
  });
}

export function useCreateKennenlernen() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createKennenlernen,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: kennenlernenKeys.lists() });
      queryClient.invalidateQueries({ queryKey: kennenlernenKeys.kanban() });
      queryClient.invalidateQueries({ queryKey: kennenlernenKeys.statistics() });
      // The backend moves the Bedarf to TERMINIERT when an appointment is created,
      // so every Bedarf view (list, kanban, detail) is stale at this point.
      queryClient.invalidateQueries({ queryKey: bedarfeKeys.all });
      // Also invalidate related bedarf and bewerber queries
      if (data.data.bedarfId) {
        queryClient.invalidateQueries({ queryKey: kennenlernenKeys.byBedarf(data.data.bedarfId) });
      }
      if (data.data.bewerberId) {
        queryClient.invalidateQueries({ queryKey: kennenlernenKeys.byBewerber(data.data.bewerberId) });
      }
    },
  });
}

export function useUpdateKennenlernen() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateKennenlernen,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: kennenlernenKeys.lists() });
      queryClient.invalidateQueries({ queryKey: kennenlernenKeys.kanban() });
      queryClient.invalidateQueries({ queryKey: kennenlernenKeys.detail(data.data.id) });
      queryClient.invalidateQueries({ queryKey: kennenlernenKeys.statistics() });
      // Also invalidate related bedarf and bewerber queries
      if (data.data.bedarfId) {
        queryClient.invalidateQueries({ queryKey: kennenlernenKeys.byBedarf(data.data.bedarfId) });
      }
      if (data.data.bewerberId) {
        queryClient.invalidateQueries({ queryKey: kennenlernenKeys.byBewerber(data.data.bewerberId) });
      }
    },
  });
}

export function useDeleteKennenlernen() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteKennenlernen,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: kennenlernenKeys.lists() });
      queryClient.invalidateQueries({ queryKey: kennenlernenKeys.kanban() });
      queryClient.invalidateQueries({ queryKey: kennenlernenKeys.statistics() });
    },
  });
}
